import type { PlannerProject, WardrobeModule } from "../types";
import { resolveDecor } from "./decors";
import { eggerDecor } from "./eggerDecors";
import { facadePaintHex, facadePaintLabel } from "./facades";

// ---------------------------------------------------------------------------
// Кромка корпуса (видео Сергея 24.10): как корпус, как фасады или свой декор.
// Частый случай — корпус чёрный, фасады белые: в зазорах между закрытыми
// фасадами видны торцы корпуса, поэтому кромку делают белой, в цвет фасадов.

/** «facade» — в цвет фасадов, артикул Egger — свой декор, нет поля — как корпус. */
export const EDGE_FINISH_FACADE = "facade";

export const normalizeEdgeFinish = (value: unknown): string | undefined =>
  value === EDGE_FINISH_FACADE ? EDGE_FINISH_FACADE : eggerDecor(value)?.code;

/** Отделка, в цвет которой идут фасады изделия: у стеллажа и комода — по фасадам ячеек. */
const facadeSource = (module: WardrobeModule) => {
  if (module.type !== "shelving") return module.door === "open" ? null : module;
  const cells = (module.shelvingCells ?? []).filter((cell) => cell.content === "door" || cell.content === "drawers");
  return cells.find((cell) => cell.facade !== "glass" && cell.facade !== "mirror") ?? null;
};

/**
 * Во что красится кромка: decor — артикул декора, color — цвет эмали. null —
 * как корпус (или фасадов нет, у стекла и зеркала своей кромки нет).
 */
export const edgeFinishTarget = (project: PlannerProject, module: WardrobeModule):
  { kind: "decor"; decor: string } | { kind: "color"; hex: string; label: string } | null => {
  const edge = normalizeEdgeFinish(module.edgeFinish);
  if (!edge) return null;
  const carcass = module.decor ?? project.carcassDecor;
  if (edge !== EDGE_FINISH_FACADE) return edge === carcass ? null : { kind: "decor", decor: edge };
  const source = facadeSource(module);
  if (!source) return null;
  const facade = source.facade ?? "wood";
  if (facade === "satin" || facade === "gloss") {
    return { kind: "color", hex: facadePaintHex(source), label: facadePaintLabel(source) };
  }
  if (facade === "wood") {
    const decor = ("facadeDecor" in source ? source.facadeDecor : undefined) ?? carcass;
    return decor && decor !== carcass ? { kind: "decor", decor } : null;
  }
  return null;
};

/** Подпись для техкарты и сметы: «кромка в цвет фасадов (Белый)», «кромка Белый премиум W1100 ST9». */
export const edgeFinishLabel = (project: PlannerProject, module: WardrobeModule) => {
  const edge = normalizeEdgeFinish(module.edgeFinish);
  if (!edge) return null;
  const target = edgeFinishTarget(project, module);
  if (!target) return edge === EDGE_FINISH_FACADE ? "кромка в цвет фасадов (как корпус)" : null;
  if (target.kind === "color") return `кромка в цвет фасадов (${target.label})`;
  const decor = resolveDecor(target.decor);
  return edge === EDGE_FINISH_FACADE
    ? `кромка в декоре фасадов (${decor.name} ${decor.code})`
    : `кромка ${decor.name} ${decor.code}`;
};
